import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, Trash2, Edit, Eye, Filter, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import { getMaterialTypeIcon, getMaterialTypeInfo, getMaterialTypeOptions } from '../../utils/materialTypes';
// Import the DocumentViewer component
import DocumentViewer from '../../components/DocumentViewer'; // Adjust the path as needed

const MyMaterialsPage = () => {
  const { user } = useContext(AuthContext);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  // Add state to track which material is being viewed
  const [viewingMaterial, setViewingMaterial] = useState(null);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${user.id}/materials`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch materials');
        }

        const data = await response.json();
        setMaterials(data.materials || []);
      } catch (error) {
        console.error('Error fetching materials:', error);
        toast.error('Failed to load your materials');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchMaterials();
    }
  }, [user?.id]);

  const handleDelete = async (materialId) => {
    // Close modal immediately and show loading state
    setDeleteConfirmation(null);
    toast.loading('Deleting material...', { id: `delete-${materialId}` });
    
    // Remove the material from UI immediately for better UX
    const materialToDelete = materials.find(m => m.id === materialId);
    setMaterials(materials.filter(material => material.id !== materialId));
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/materials/${materialId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        // If delete failed, restore the material to the list
        setMaterials(prevMaterials => [...prevMaterials, materialToDelete].sort((a, b) => b.createdAt - a.createdAt));
        throw new Error('Failed to delete material');
      }

      toast.success('Material deleted successfully', { id: `delete-${materialId}` });
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error('Failed to delete material', { id: `delete-${materialId}` });
    }
  };


  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Filter materials based on selected type
  const filteredMaterials = filter === 'all' 
    ? materials 
    : materials.filter(material => material.type === filter);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Materials</h1>
        <Link 
          to="/my-courses" 
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <Upload size={18} className="mr-2" />
          Upload New Material
        </Link>
      </div>

      {/* Filter options */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-2">
          <Filter size={18} className="text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by type:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm ${
              filter === 'all' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
            }`}
          >
            All
          </button>
          {getMaterialTypeOptions(false).map((option) => (
            <button 
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-3 py-1 rounded-full text-sm ${
                filter === option.value 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      ) : filteredMaterials.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Material
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Course
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date Uploaded
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Stats
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredMaterials.map((material) => (
                <tr key={material.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                        {getMaterialTypeIcon(material.type)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {material.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {getMaterialTypeInfo(material.type).label}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{material.courseName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{material.courseCode}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(material.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <span className="mr-2">👍 {material.upvotes || 0}</span>
                      <span>👎 {material.downvotes || 0}</span>
                    </div>
                    
                    {material.reportCount > 0 && (
                      <div className="mt-1 flex items-center text-sm text-red-500">
                        <AlertTriangle size={14} className="mr-1" />
                        <span>Reported ({material.reportCount})</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {/* Change the anchor tag to a button that opens the DocumentViewer */}
                      <button 
                        onClick={() => setViewingMaterial(material)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="View"
                      >
                        <Eye size={18} />
                      </button>
                      
                      <Link 
                        to={`/materials/${material.id}/edit`}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </Link>
                      
                      <button 
                        onClick={() => setDeleteConfirmation(material.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <FileText size={48} className="mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No Materials Found</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            {filter !== 'all' 
              ? `You haven't uploaded any ${filter} materials yet.` 
              : "You haven't uploaded any materials yet."}
          </p>
          <Link
            to="/courses"
            className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <Upload size={18} className="mr-2" />
            Upload Your First Material
          </Link>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Delete Material
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this material? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmation)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document viewer modal */}
      {viewingMaterial && (
        <DocumentViewer
          onClose={() => setViewingMaterial(null)}
          title={viewingMaterial.title}
          fileType={viewingMaterial.fileType || ''} // Provide a default if fileType is not available
          fileUrl={viewingMaterial.fileUrl}
          token={localStorage.getItem('token')}
          materialId={viewingMaterial.id}
          currentUser={user}
        />
      )}
    </div>
  );
};

export default MyMaterialsPage;